
import React, { useState, useRef } from 'react';

interface BeforeAfterSliderProps {
  before: { url: string; label: string; stats?: string };
  after: { url: string; label: string; stats?: string };
}

export const BeforeAfterSlider = ({ before, after }: BeforeAfterSliderProps) => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging && e.type !== 'mousemove') return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const position = ((x - rect.left) / rect.width) * 100;
      setSliderPos(Math.min(Math.max(position, 0), 100));
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative aspect-[3/4] w-full max-w-lg mx-auto rounded-[2.5rem] overflow-hidden shadow-2xl cursor-ew-resize select-none border-4 border-slate-900"
      onMouseMove={handleMove}
      onTouchMove={handleMove}
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onTouchStart={() => setIsDragging(true)}
      onTouchEnd={() => setIsDragging(false)}
    >
      {/* After Image (Background) */}
      <img src={after.url} className="absolute inset-0 w-full h-full object-cover" alt="After" />
      <div className="absolute bottom-6 right-6 z-10 bg-black/60 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10">
        <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">{after.label}</p>
        <p className="text-xs font-bold text-white">{after.stats}</p>
      </div>

      {/* Before Image (Foreground with Clip) */}
      <div 
        className="absolute inset-0 w-full h-full overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
      >
        <img src={before.url} className="absolute inset-0 w-full h-full object-cover" alt="Before" />
        <div className="absolute bottom-6 left-6 z-10 bg-black/60 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{before.label}</p>
          <p className="text-xs font-bold text-white">{before.stats}</p>
        </div>
      </div>

      {/* Slider Bar */}
      <div 
        className="absolute inset-y-0 z-20 w-1 bg-white shadow-[0_0_15px_rgba(255,255,255,0.5)]"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-2xl border-4 border-slate-950">
          <div className="flex gap-1 text-slate-950 font-black">
            <span>‹</span><span>›</span>
          </div>
        </div>
      </div>
    </div>
  );
};
